/**
 * Mutation hooks for the agent Tasks list (plan
 * plans/2026-08-08-reminders-and-tasks.md section 5).
 *
 * Wraps:
 *   POST   /api/v1/agent/tasks              -> useCreateTask
 *   PATCH  /api/v1/agent/tasks/{id}         -> useUpdateTask
 *   POST   /api/v1/agent/tasks/{id}/dismiss -> useDismissTask
 *   DELETE /api/v1/agent/tasks/{id}         -> useDeleteTask
 *   POST   /api/v1/agent/tasks/reorder      -> useReorderTasks
 *
 * Every mutation invalidates the shared ["tasks"] prefix on success,
 * which refreshes the open list, the done list, and the sidebar badge
 * count in one sweep. Dismissed tasks also surface in the Activity
 * feed, so dismiss additionally nudges that query.
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";

import api from "@/lib/api";
import { ACTIVITY_FEED_QUERY_KEY } from "@/hooks/useActivityFeed";
import { TASKS_QUERY_KEY } from "@/hooks/useTasks";
import type {
  TaskCreateRequest,
  TaskReorderRequest,
  TaskResponse,
  TaskUpdateRequest,
} from "@/types/api";

/** Manual portal create - lands at ordinal 1 (top) server-side. */
export function useCreateTask(): UseMutationResult<
  TaskResponse,
  Error,
  TaskCreateRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: TaskCreateRequest) => {
      const response = await api.post<TaskResponse>("/agent/tasks", body);
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TASKS_QUERY_KEY });
    },
  });
}

export interface UpdateTaskInput {
  taskId: string;
  body: TaskUpdateRequest;
}

/** Partial edit. Omitted keys leave fields alone; explicit null
 * clears due_date / ticket_ref / description_html. */
export function useUpdateTask(): UseMutationResult<
  TaskResponse,
  Error,
  UpdateTaskInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskId, body }: UpdateTaskInput) => {
      const response = await api.patch<TaskResponse>(
        `/agent/tasks/${taskId}`,
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TASKS_QUERY_KEY });
    },
  });
}

/** Mark done - the one terminal state. The row survives forever in
 * the Done view and the Activity feed. */
export function useDismissTask(): UseMutationResult<
  TaskResponse,
  Error,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (taskId: string) => {
      const response = await api.post<TaskResponse>(
        `/agent/tasks/${taskId}/dismiss`,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TASKS_QUERY_KEY });
      void queryClient.invalidateQueries({
        queryKey: ACTIVITY_FEED_QUERY_KEY,
      });
    },
  });
}

/** Hard remove - row, attachments, and Spaces objects all go, and no
 * Activity trace is left. The UI confirms before calling this. */
export function useDeleteTask(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (taskId: string) => {
      await api.delete(`/agent/tasks/${taskId}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TASKS_QUERY_KEY });
    },
  });
}

/**
 * Full-list drag reorder. ``ordered_ids`` must be exactly the current
 * open set; the server 409s when the drag raced a create or dismiss,
 * and the page reacts by toasting + refetching. Invalidation runs on
 * settled (not just success) so a 409 also snaps the list back to
 * server truth.
 */
export function useReorderTasks(): UseMutationResult<
  TaskResponse[],
  Error,
  string[]
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orderedIds: string[]) => {
      const body: TaskReorderRequest = { ordered_ids: orderedIds };
      const response = await api.post<TaskResponse[]>(
        "/agent/tasks/reorder",
        body,
      );
      return response.data;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: TASKS_QUERY_KEY });
    },
  });
}
